/*
 * Support > Finance: tabs, period presets, table columns.
 * All amounts come from the server (controllers/supportAdmin/finance/*).
 */

export const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'organizers', label: 'By organizer' },
    { key: 'listings', label: 'By trip / listing' },
    { key: 'payments', label: 'Payments received' },
    { key: 'refunds', label: 'Refunds' },
    { key: 'payouts', label: 'Payouts & bills' },
    { key: 'tax', label: 'Tax / CA pack' }
];

export const TYPES = [
    { value: '', label: 'All types' },
    { value: 'trip', label: 'Trips' },
    { value: 'act', label: 'Activities' },
    { value: 'gu', label: 'Guides' },
    { value: 'camp', label: 'Camps / stays' }
];

export const TYPE_LABEL = { trip: 'Trip', act: 'Activity', gu: 'Guide', camp: 'Camp' };

export const BOOKED_VIA = [
    { value: '', label: 'All channels' },
    { value: 'Website', label: 'Website' },
    { value: 'App', label: 'App' },
    { value: 'Manual', label: 'Manual (organizer collected)' }
];

const DAY = 24 * 60 * 60 * 1000;
const iso = (d) => new Date(d.getTime() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);

// Indian financial year: 1 Apr – 31 Mar
export function fyOf(date = new Date()) {
    const d = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
    const y = d.getUTCMonth() >= 3 ? d.getUTCFullYear() : d.getUTCFullYear() - 1;
    return { from: `${y}-04-01`, to: `${y + 1}-03-31`, label: `FY ${y}-${String(y + 1).slice(2)}` };
}

export function presetRange(key, now = new Date()) {
    const today = iso(now);
    switch (key) {
        case 'today':
            return { from: today, to: today };
        case 'week':
            return { from: iso(new Date(now.getTime() - 6 * DAY)), to: today };
        case 'month':
            return { from: `${today.slice(0, 8)}01`, to: today };
        case 'year': {
            const fy = fyOf(now);
            return { from: fy.from, to: today };
        }
        default:
            return null;
    }
}

export const PRESETS = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'Last 7 days' },
    { key: 'month', label: 'This month' },
    { key: 'year', label: 'This financial year' },
    { key: 'custom', label: 'Custom' }
];

export const rupee = (n) =>
    `${Number(n) < 0 ? '−' : ''}₹${Math.abs(Math.round(Number(n) || 0)).toLocaleString('en-IN')}`;
export const day = (v) =>
    v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

// table columns: key, label, kind ('money' | 'date' | 'text' | 'pct' | 'num'), total (sum in footer)
export const COLUMNS = {
    organizers: [
        { key: 'name', label: 'Organizer' },
        { key: 'type', label: 'Type', kind: 'type' },
        { key: 'gstin', label: 'GSTIN', kind: 'gstin' },
        { key: 'bookings', label: 'Bookings', kind: 'num', total: true },
        { key: 'sales', label: 'Sales', kind: 'money', total: true },
        { key: 'received', label: 'Received', kind: 'money', total: true },
        { key: 'refunds', label: 'Refunds', kind: 'money', total: true },
        { key: 'commissionPct', label: 'Comm. %', kind: 'pct' },
        { key: 'commission', label: 'Commission', kind: 'money', total: true },
        { key: 'gstOnCommission', label: 'GST on comm.', kind: 'money', total: true },
        { key: 'tds', label: 'TDS', kind: 'money', total: true },
        { key: 'paidOut', label: 'Paid out', kind: 'money', total: true },
        { key: 'pending', label: 'Still owed', kind: 'money', total: true }
    ],
    listings: [
        { key: 'title', label: 'Trip / listing' },
        { key: 'type', label: 'Type', kind: 'type' },
        { key: 'organizer', label: 'Organizer' },
        { key: 'bookings', label: 'Bookings', kind: 'num', total: true },
        { key: 'guests', label: 'Guests', kind: 'num', total: true },
        { key: 'sales', label: 'Sales', kind: 'money', total: true },
        { key: 'received', label: 'Received', kind: 'money', total: true },
        { key: 'refunds', label: 'Refunds', kind: 'money', total: true },
        { key: 'commission', label: 'Commission', kind: 'money', total: true }
    ],
    payments: [
        { key: 'date', label: 'Date', kind: 'date' },
        { key: 'bookingId', label: 'Booking', kind: 'id' },
        { key: 'type', label: 'Type', kind: 'type' },
        { key: 'listing', label: 'Trip / listing' },
        { key: 'customer', label: 'Customer' },
        { key: 'method', label: 'Method' },
        { key: 'amount', label: 'Received', kind: 'money', total: true },
        { key: 'convenienceFee', label: 'Conv. fee', kind: 'money', total: true },
        { key: 'gatewayFee', label: 'Gateway fee', kind: 'money', total: true },
        { key: 'gatewayGst', label: 'GST on fee', kind: 'money', total: true },
        { key: 'settled', label: 'To bank', kind: 'money', total: true },
        { key: 'paymentId', label: 'Razorpay ID', kind: 'id' }
    ],
    refunds: [
        { key: 'date', label: 'Date', kind: 'date' },
        { key: 'bookingId', label: 'Booking', kind: 'id' },
        { key: 'type', label: 'Type', kind: 'type' },
        { key: 'listing', label: 'Trip / listing' },
        { key: 'customer', label: 'Customer' },
        { key: 'cancelledBy', label: 'Cancelled by' },
        { key: 'paid', label: 'Paid', kind: 'money', total: true },
        { key: 'refund', label: 'Refunded', kind: 'money', total: true },
        { key: 'kept', label: 'Kept', kind: 'money', total: true },
        { key: 'status', label: 'Status', kind: 'status' },
        { key: 'refundId', label: 'Refund ID', kind: 'id' }
    ],
    payoutsPaid: [
        { key: 'date', label: 'Paid on', kind: 'date' },
        { key: 'organizer', label: 'Organizer' },
        { key: 'type', label: 'Type', kind: 'type' },
        { key: 'bookings', label: 'Bookings', kind: 'num', total: true },
        { key: 'gross', label: 'Gross', kind: 'money', total: true },
        { key: 'commission', label: 'Commission', kind: 'money', total: true },
        { key: 'gst', label: 'GST on comm.', kind: 'money', total: true },
        { key: 'tds', label: 'TDS', kind: 'money', total: true },
        { key: 'amount', label: 'Paid', kind: 'money', total: true },
        { key: 'bill', label: '', kind: 'bill' }
    ],
    payoutsPending: [
        { key: 'organizer', label: 'Organizer' },
        { key: 'type', label: 'Type', kind: 'type' },
        { key: 'bookings', label: 'Bookings', kind: 'num', total: true },
        { key: 'since', label: 'Oldest trip end', kind: 'date' },
        { key: 'gross', label: 'Gross', kind: 'money', total: true },
        { key: 'commission', label: 'Commission', kind: 'money', total: true },
        { key: 'gst', label: 'GST on comm.', kind: 'money', total: true },
        { key: 'tds', label: 'TDS', kind: 'money', total: true },
        { key: 'amount', label: 'To pay', kind: 'money', total: true }
    ],
    taxMonths: [
        { key: 'month', label: 'Month' },
        { key: 'sales', label: 'Sales (GMV)', kind: 'money', total: true },
        { key: 'commission', label: 'Commission (taxable)', kind: 'money', total: true },
        { key: 'gstOnCommission', label: 'Output GST', kind: 'money', total: true },
        { key: 'gatewayGst', label: 'Input GST (gateway)', kind: 'money', total: true },
        { key: 'netGst', label: 'Net GST payable', kind: 'money', total: true },
        { key: 'tds', label: 'TDS deducted', kind: 'money', total: true },
        { key: 'refunds', label: 'Refunds', kind: 'money', total: true }
    ]
};
